import React from 'react';
import capitalize from 'lodash/capitalize';
import classNameBind from 'classnames/bind';
import moment from 'moment';
import MediaQuery from 'react-responsive';
import getUrlWithCacheBusterFromJwt from '../../utils/methods/getUrlWithCacheBusterFromJwt';
import { withTranslation } from '../Translator';
import Icon from '../Icon';
import Logo from './components/Logo';
import Button from '../Button';
import { Menu } from './components/Menu';
import styles from './ProductBar.module.css';
import { Buttons } from './components/Buttons';
import getJwtPayload from '../../utils/methods/getJwtPayload';
import useDataLayer from '../../utils/methods/useDataLayer';
import getCheckoutLink from '../../utils/methods/getCheckoutLink';

const Icons = Icon.propEnums.name;
const RETRY_FETCH_LIMIT = 5;
const RETRY_FETCH_INTERVAL = 5000;
const UPGRADE_BTN_DATA_ID = 'ProductBar.UpgradeButton';
const PPMS_PLATFORM_CONFIG_API_URL = '/user-panel/config.json';
const TRAFFIC_USAGE_API_URL = '/api/traffic-monitor/v2/usage';
const bindClasses = classNameBind.bind(styles);
const dataLayer = useDataLayer();

class ProductBar extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      menuItemsLoaded: false,
      menuItemsError: false,
      menuItemsValidationError: false,
      trafficUsageLoaded: false,
      trafficUsageLoadingError: false,
      trafficUsage: 0,
      menuItems: {},
      ppasVersion: null,
    };
  }

  componentDidMount() {
    const isPayingLicense = this.props.licenseType === 'paying';
    const isOwner = this.props.userRole === 'OWNER';

    this.fetchConfigWithRetryOnError();
    this.fetchPPASVersion();
    if (!isPayingLicense || isOwner) {
      this.fetchTraffic();
    }
  }

  componentDidUpdate() {
    const { ppasVersion } = this.state;
    if (ppasVersion !== null && dataLayer.every(item => !item.ppasVersion)) {
      dataLayer.push({
        ppasVersion,
      });
    }
  }

  getRequestConfig = () => ({
    method: 'GET',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      Authorization: `Bearer ${this.props.jwt}`,
    },
  });

  getPreparedLinks = (type) => {
    const rawLinks = this.state.menuItems[type];
    const internalLink = /^[/,?](.*)/;

    const preparedLinks = rawLinks.filter(link => link.id !== 'profile').map((link) => {
      const linkName = link.id.replace(/_/g, '');
      const icon = type === 'apps' && Icons.includes(`app-${linkName}`)
        ? {
          name: `app-${linkName}`,
          color: 'default',
          size: 'medium',
        }
        : undefined;
      const options = { href: link.href };

      if (!internalLink.test(link.href)) {
        options.target = '_blank';
      }

      return {
        name: this.props.t(this.createTranslationKey(linkName)),
        options,
        icon,
        newBadge: false,
        betaBadge: false,
      };
    });

    return preparedLinks;
  };

  renderUpgradeButton = (small = false) => {
    const { t } = this.props;
    const { jwt } = this.props;
    const organizationName = getJwtPayload(jwt).org;

    return (
      <Button
        dataId={UPGRADE_BTN_DATA_ID}
        appearance="simple"
        text={small ? null : t('upgradeLabel')}
        onClick={() => window.open(getCheckoutLink({ organization: organizationName }), '_blank')}
        icon={small ? 'rocket' : null}
      />
    );
  };

  fetchConfigWithRetryOnError = () => {
    let retryCounter = 0;
    const urlWithCacheBuster = getUrlWithCacheBusterFromJwt(this.props.ppmsProductBarApiUrl, this.props.jwt);
    const fetchConfig = () => (
      fetch(urlWithCacheBuster, this.getRequestConfig())
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          this.setState({
            menuItemsLoaded: true,
            menuItemsError: true,
            menuItemsValidationError: false,
          });
          retryCounter += 1;
          if (retryCounter < RETRY_FETCH_LIMIT) {
            setTimeout(fetchConfig, RETRY_FETCH_INTERVAL);
          }

          return null;
        })
        .then((result) => {
          if (result) {
            if (this.isResponseSchemaValid(result.data.attributes)) {
              this.setState({
                menuItemsLoaded: true,
                menuItemsError: false,
                menuItemsValidationError: false,
                menuItems: result.data.attributes,
              });
            } else {
              this.setState({
                menuItemsLoaded: true,
                menuItemsError: false,
                menuItemsValidationError: true,
              });
            }
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('JavaScript code error occured: ', error);
        })
    );

    return fetchConfig();
  };

  /*
  todo: add schema validation like joi
  see: https://github.com/PiwikPRO/PPMS-UIComponents/pull/544
  */
  isResponseSchemaValid = () => true;

  createTranslationKey = linkName => (
    `linkName${capitalize(linkName)}`
  );

  async fetchTraffic() {
    const currentMonth = moment(new Date()).format('YYYY-MM');
    const trafficApiPath = `${TRAFFIC_USAGE_API_URL}?from=${currentMonth}`;

    try {
      this.setState(state => ({
        ...state,
        trafficUsageLoading: true,
      }));

      const response = await fetch(trafficApiPath, this.getRequestConfig());

      if (!response.ok) throw new Error();

      const data = (await response.json()).data[0].total_traffic;
      this.setState(state => ({
        ...state,
        trafficUsage: data,
        trafficUsageLoading: false,
      }));
    } catch {
      this.setState(state => ({
        ...state,
        trafficUsageLoading: false,
        trafficUsageLoadingError: true,
      }));
    }
  }

  async fetchPPASVersion() {
    try {
      const response = await (await fetch(PPMS_PLATFORM_CONFIG_API_URL)).json();

      this.setState(state => ({
        ...state,
        ppasVersion: response.PPAS_VERSION,
      }));
    // eslint-disable-next-line no-empty
    } catch {}
  }

  render() {
    const {
      email,
      logoLabel,
      websitePicker,
      debugButton,
      publishButton,
      licenseType,
      isBeta,
      isNew,
      t,
      trafficLimit,
      userRole,
      onLogout,
      licenseError,
    } = this.props;

    const { ppasVersion, menuItemsLoaded, menuItemsError, menuItemsValidationError } = this.state;
    const linksReceived = menuItemsLoaded && !menuItemsError && !menuItemsValidationError;
    const apps = linksReceived ? this.getPreparedLinks('apps') : [];
    const showUpgradeButton = licenseType === 'core';
    const commonLinks = linksReceived ? this.getPreparedLinks('common') : [];
    const getProfileLink = () => {
      const filteredLink = linksReceived && this.state.menuItems.common.filter(link => link.id === 'profile');
      if (!filteredLink || filteredLink.length === 0) return '';

      return filteredLink[0].href;
    };

    return (
      <div
        className={bindClasses('wrapper')}
        data-ppuc-id="ProductBar"
      >
        <div className={bindClasses('inner')}>
          <Menu
            itemsLoaded={menuItemsLoaded}
            menuItemsError={menuItemsError}
            email={email}
            productMenuItems={apps}
            contextMenuItems={commonLinks}
            ppasVersion={ppasVersion}
            profileLink={getProfileLink()}
            onLogout={onLogout}
            trafficLoading={this.state.trafficUsageLoading}
            trafficError={this.state.trafficUsageLoadingError}
            traffic={this.state.trafficUsage}
            trafficLimit={trafficLimit}
            userRole={userRole}
            licenseType={licenseType}
            licenseError={licenseError}
          />
          <div className={bindClasses('logo')}>
            <Logo
              logoLabel={logoLabel}
              isBeta={isBeta}
              isNew={isNew}
              t={t}
            />
          </div>
        </div>
        <div className={bindClasses('inner', 'flex')}>
          {showUpgradeButton && (
          <div className={bindClasses('upgrade-btn')}>
            <MediaQuery minWidth={960}>
              {this.renderUpgradeButton()}
            </MediaQuery>
            <MediaQuery maxWidth={959}>
              {this.renderUpgradeButton(true)}
            </MediaQuery>
          </div>
          )}
          <div className={bindClasses('element')}>
            {websitePicker}
          </div>
          <div className={bindClasses('element')}>
            <Buttons
              {...{
                debugButton,
                publishButton,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

ProductBar.defaultProps = {
  websitePicker: null,
  debugButton: null,
  publishButton: null,
  ppmsProductBarApiUrl: '/api/organization/v1/product-bar',
  isBeta: false,
  isNew: false,
};

const enhanced = withTranslation('components')(ProductBar);
enhanced.displayName = 'ProductBar';

export default enhanced;
export {
  ProductBar,
};
